/**
 * Credentials Service
 * Handles Verifiable Credentials (VC) operations for VC-gated casts
 */

import axios from 'axios';
import constants from '../../constant';
import { ethers } from 'ethers';

const authApi = axios.create({
  baseURL: `${constants.authApiBaseUrl}/api/v1`,
});

// Attach JWT token to requests
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

/**
 * Issue a credential to a DID for a specific room/cast
 * @param {string} did - The DID to issue credential to
 * @param {string} roomId - The room/cast ID
 * @param {string} accessLevel - Access level (participant, speaker, host)
 * @param {string[]} permissions - List of permissions
 * @param {string|null} expiresAt - Optional expiry date (ISO string)
 * @returns {Promise<object>} - Issued credential result
 */
export const issueCredential = async (did, roomId, accessLevel = 'participant', permissions = ['view', 'chat'], expiresAt = null) => {
  const payload = {
    did,
    roomId,
    accessLevel,
    permissions,
  };
  if (expiresAt) {
    payload.expiresAt = expiresAt;
  }
  const response = await authApi.post('/credentials/issue', payload);
  return response.data;
};

/**
 * Issue credentials to multiple DIDs
 * @param {number} roomId - The room/cast ID
 * @param {Array<{did: string, accessLevel: string, permissions: string[]}>} recipients - List of recipients
 * @returns {Promise<object[]>} - Array of issued credentials
 */
export const bulkIssueCredentials = async (roomId, recipients) => {
  const results = [];
  for (const recipient of recipients) {
    try {
      const result = await issueCredential(
        recipient.did,
        roomId,
        recipient.accessLevel || 'participant',
        recipient.permissions || ['view', 'chat']
      );
      results.push({ success: true, did: recipient.did, result });
    } catch (error) {
      results.push({ success: false, did: recipient.did, error: error.message });
    }
  }
  return results;
};

/**
 * Verify if a DID has valid credentials for a room
 * @param {string} did - The DID to verify
 * @param {number} roomId - The room/cast ID
 * @returns {Promise<object>} - Verification result
 */
export const verifyCredential = async (did, roomId) => {
  const response = await authApi.post('/credentials/verify', {
    did,
    roomId,
  });
  return response.data;
};

/**
 * Get all credentials for a specific room
 * @param {number} roomId - The room/cast ID
 * @returns {Promise<object[]>} - List of credentials
 */
export const getCredentialsByRoom = async (roomId) => {
  const response = await authApi.get(`/credentials/room/${roomId}`);
  return response.data;
};

/**
 * Get all credentials for a specific DID
 * @param {string} did - The DID
 * @returns {Promise<object[]>} - List of credentials
 */
export const getCredentialsByDid = async (did) => {
  const response = await authApi.get(`/credentials/did/${encodeURIComponent(did)}`);
  return response.data;
};

/**
 * Revoke a credential
 * @param {string} did - The DID whose credential to revoke
 * @param {number} roomId - The room/cast ID
 * @param {string} reason - Reason for revocation
 * @returns {Promise<object>} - Revoked credential
 */
export const revokeCredential = async (did, roomId, reason = '') => {
  const response = await authApi.post('/credentials/revoke', {
    did,
    roomId,
    reason,
  });
  return response.data;
};

/**
 * Get room access status for a DID
 * @param {string} did - The DID
 * @param {number} roomId - The room/cast ID
 * @returns {Promise<object>} - Access status
 */
export const getRoomAccess = async (did, roomId) => {
  const response = await authApi.get(`/credentials/access/${encodeURIComponent(did)}/${roomId}`);
  return response.data;
};

/**
 * Generate nonce for DID authentication
 * @param {string} did - The DID
 * @returns {Promise<object>} - Nonce data
 */
export const generateNonce = async (did) => {
  const response = await authApi.post('/auth/did/nonce', { did });
  return response.data;
};

/**
 * Verify signed nonce for DID authentication
 * @param {string} did - The DID
 * @param {string} signature - The signed nonce
 * @returns {Promise<object>} - Authentication result with token
 */
export const verifyDidSignature = async (did, signature) => {
  const response = await authApi.post('/auth/did/login', { did, signature });
  return response.data;
};

/**
 * Sign a message with connected wallet
 * @param {string} message - Message to sign
 * @returns {Promise<{signature: string, address: string}>} - Signature and address
 */
export const signWithWallet = async (message) => {
  if (!window.ethereum) {
    throw new Error('No wallet detected. Please install MetaMask or another Web3 wallet.');
  }

  const provider = new ethers.BrowserProvider(window.ethereum);
  const signer = await provider.getSigner();
  const address = await signer.getAddress();

  // Sign using EIP-191 personal sign
  const signature = await signer.signMessage(message);

  return { signature, address };
};

/**
 * Get DID from wallet address
 * @param {string} address - Ethereum address
 * @returns {string} - DID string
 */
export const getDIDFromAddress = (address) => {
  return `did:ethr:${address.toLowerCase()}`;
};

/**
 * Complete VC verification flow for joining a cast
 * 1. Connect wallet
 * 2. Get nonce
 * 3. Sign nonce
 * 4. Verify signature
 * 5. Check room access
 *
 * @param {number} roomId - The room/cast ID to join
 * @returns {Promise<{verified: boolean, accessLevel: string, permissions: string[], token: string}>}
 */
export const verifyAccessWithWallet = async (roomId) => {
  // Step 1: Connect wallet and get address
  if (!window.ethereum) {
    throw new Error('No wallet detected. Please install MetaMask or another Web3 wallet.');
  }

  const provider = new ethers.BrowserProvider(window.ethereum);
  await provider.send('eth_requestAccounts', []);
  const signer = await provider.getSigner();
  const address = await signer.getAddress();
  const did = getDIDFromAddress(address);

  // Step 2: Get nonce from server
  const nonceData = await generateNonce(did);
  const nonce = nonceData.nonce;

  // Step 3: Sign the nonce
  const message = `Sign this message to verify your identity for Decast.\n\nNonce: ${nonce}`;
  const signature = await signer.signMessage(message);

  // Step 4: Verify signature with server
  const authResult = await verifyDidSignature(did, signature);

  // Store the token if returned
  if (authResult.token) {
    localStorage.setItem('vcAccessToken', authResult.token);
  }

  // Step 5: Verify room access
  const accessResult = await verifyCredential(did, roomId);

  return {
    verified: accessResult.valid,
    did,
    address,
    accessLevel: accessResult.accessLevel,
    permissions: accessResult.permissions,
    storagePath: accessResult.storagePath,
    storageType: accessResult.storageType,
    expiresAt: accessResult.expiresAt,
  };
};

/**
 * Quick check if user has access (without full verification)
 * @param {string} address - Wallet address
 * @param {number} roomId - Room ID
 * @returns {Promise<boolean>}
 */
export const quickAccessCheck = async (address, roomId) => {
  try {
    const did = getDIDFromAddress(address);
    const result = await verifyCredential(did, roomId);
    return result.valid;
  } catch {
    return false;
  }
};

export default {
  issueCredential,
  bulkIssueCredentials,
  verifyCredential,
  getCredentialsByRoom,
  getCredentialsByDid,
  revokeCredential,
  getRoomAccess,
  generateNonce,
  verifyDidSignature,
  signWithWallet,
  getDIDFromAddress,
  verifyAccessWithWallet,
  quickAccessCheck,
};
